import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { IconButton } from '@doger/ui/components/ui/button';
import { Link } from '@doger/ui/components/ui/link';
import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';
import { $$api } from '@doger/core/shared/api';

import { usePendingAuth } from '../use-pending-auth.hook';
import styles from './sign-in-form.module.scss';

type SignInFormProps = {
  className?: string;
  onSuccess: (tokens: { accessToken: string; refreshToken: string }) => void;
};

export const SignInForm = ({ className, onSuccess }: SignInFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();
  const isPendingAuth = usePendingAuth();
  const { t } = useTranslation('core');

  const signInSchema = z.object({
    email: z.email(t('validation.auth.emailInvalid')),
    password: z.string().min(1, t('validation.auth.passwordRequired')),
  });

  const signIn = useMutation({
    mutationKey: ['auth', 'signin', 'local'],
    mutationFn: async ({ email, password }: z.infer<typeof signInSchema>) =>
      $$api.login({
        email,
        password,
      }),

    onSuccess: ({ accessToken, refreshToken }) => {
      $$api.tokensService.setTokens(accessToken, refreshToken);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      onSuccess({ accessToken, refreshToken });
    },

    onError: () => {
      $$toast.error(t('auth.forms.signIn.actions.failed'));
    },
  });

  const form = useAppForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      await signIn.mutateAsync(value);
    },
    validators: {
      onSubmit: signInSchema,
      onChange: signInSchema,
    },
  });

  return (
    <form
      name='sign-in-form'
      className={className}
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <div className={styles.fields}>
        <form.AppForm>
          <form.AppField
            name='email'
            children={(field) => (
              <field.TextField
                label={t('auth.forms.signUp.fields.email.label')}
                variant='contained'
                type='email'
                placeholder={t('auth.forms.signUp.fields.email.placeholder')}
              />
            )}
          />
        </form.AppForm>

        <form.AppForm>
          <form.AppField
            name='password'
            children={(field) => (
              <field.TextField
                label={t('auth.forms.signUp.fields.password.label')}
                variant='contained'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.forms.signUp.fields.password.placeholder')}
                autoComplete='current-password'
                rightAdornment={
                  <IconButton
                    type='button'
                    variant='text'
                    size='md'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </IconButton>
                }
                data-clarity-mask='true'
              />
            )}
          />
        </form.AppForm>

        <div className={styles.forgotPasswordLink}>
          <Link className={styles.link} to='/reset-password/request'>
            {t('auth.forgotPassword.title')}
          </Link>
        </div>
      </div>

      <form.AppForm>
        <form.SubmitButton
          variant='contained'
          size='lg'
          palette='primary'
          disabled={({ canSubmit, isSubmitting, isValid }) =>
            isPendingAuth || !canSubmit || isSubmitting || !isValid
          }
          children={({ isSubmitting }) =>
            isSubmitting
              ? t('auth.forms.signIn.actions.submitting')
              : t('auth.forms.signIn.actions.submit')
          }
        />
      </form.AppForm>
    </form>
  );
};
